import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import StatusPill from "../components/StatusPill";
import { Link, useNavigate } from "react-router-dom";
import { Plus, X, Loader2, Search, Upload } from "lucide-react";
import ExportButton from "../components/ExportButton";
import ImportTasksDialog from "../components/ImportTasksDialog";
import { toast } from "sonner";

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [masters, setMasters] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const nav = useNavigate();

  const load = () => api.get("/tasks", { params: { q, status: statusFilter || undefined } })
    .then((r) => setTasks(r.data));

  useEffect(() => {
    load();
    api.get("/masters").then((r) => setMasters(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    if (user.role !== "engineer") api.get("/users", { params: { role: "engineer" } }).then((r) => setEngineers(r.data));
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const canCreate = user.role !== "engineer";

  return (
    <div>
      <PageHeader
        eyebrow={user.role === "engineer" ? "My Tasks" : "Task Management"}
        title="Tasks"
        subtitle={user.role === "engineer" ? "Tasks assigned to you across all projects." : "All tasks across engineers, projects & circles."}
        right={canCreate && (
          <div className="flex items-center gap-2 flex-wrap">
            <ExportButton endpoint="tasks.xlsx" testid="export-tasks-btn" />
            <button
              data-testid="import-tasks-btn"
              onClick={() => setShowImport(true)}
              className="border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] px-3 py-2 text-xs mono uppercase tracking-widest flex items-center gap-2 transition-colors"
            >
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
            <button
              data-testid="new-task-btn"
              onClick={() => setShowCreate(true)}
              className="bg-[#FF5722] hover:bg-[#F4511E] px-4 py-2 text-sm font-medium rounded-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Task
            </button>
          </div>
        )}
      />

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="task-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search job id, site, NE name..."
            className="w-full bg-[#0B0C10] border border-white/10 focus:border-[#FF5722] outline-none pl-10 pr-4 py-2 text-sm rounded-sm"
          />
        </div>
        <select
          data-testid="task-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#0B0C10] border border-white/10 px-4 py-2 text-sm rounded-sm"
        >
          <option value="">All statuses</option>
          {masters?.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <Card>
        {tasks.length === 0 ? (
          <EmptyState title="No tasks yet" hint={canCreate ? "Create your first task." : "You have no tasks assigned yet."} testid="empty-tasks" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead>
                <tr>
                  <th>Serial</th>
                  <th>Project · Job ID</th>
                  <th>Site</th>
                  <th>Circle</th>
                  <th>Engineer</th>
                  <th>Activity</th>
                  <th>Billing Type</th>
                  <th>Status</th>
                  <th>Billing</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id} className="cursor-pointer" onClick={() => nav(`/tasks/${t.id}`)} data-testid={`task-row-${t.id}`}>
                    <td className="mono text-[#FF5722]">
                      <div className="flex items-center gap-1.5">
                        {t.task_serial}
                        {t.is_locked && <span title="Locked" className="text-amber-400">🔒</span>}
                      </div>
                    </td>
                    <td>
                      <div className="font-medium text-white">{t.project_name}</div>
                      <div className="mono text-xs text-slate-500">{t.job_id || "—"}</div>
                    </td>
                    <td>
                      <div className="text-white">{t.site_name || "—"}</div>
                      <div className="mono text-xs text-slate-500">{t.site_id || "—"}</div>
                    </td>
                    <td className="mono">{t.circle || "—"}</td>
                    <td className="text-xs">{engineerName(engineers, t.engineer_id, user)}</td>
                    <td className="text-xs">{Array.isArray(t.activity_planned) ? t.activity_planned.join(", ") : (t.activity_planned || "—")}</td>
                    <td className="text-xs mono">{t.billing_type || t.visit_type || "—"}</td>
                    <td><StatusPill status={t.status || "Planned"} /></td>
                    <td><StatusPill status={t.billing_status || "N/A"} /></td>
                    <td className="text-right"><span className="mono text-[#FF5722] text-xs">OPEN →</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <CreateTaskDialog
          masters={masters}
          projects={projects}
          engineers={engineers}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {showImport && (
        <ImportTasksDialog
          onClose={() => setShowImport(false)}
          onImported={() => load()}
        />
      )}
    </div>
  );
}

function engineerName(engineers, engineer_id, currentUser) {
  if (!engineer_id) return "—";
  if (currentUser.id === engineer_id) return currentUser.name;
  const e = engineers.find((x) => x.id === engineer_id);
  return e?.name || "—";
}

function CreateTaskDialog({ masters, projects, engineers, onClose, onCreated }) {
  const [form, setForm] = useState({
    task_type: "New",
    date: new Date().toISOString().slice(0, 10),
    project_name: projects[0]?.name || "",
    job_id: "",
    circle: "WB",
    state: "West Bengal",
    city: "",
    site_name: "",
    site_id: "",
    eqpt_brand: "NOKIA",
    eqpt_type: "",
    eqpt_qty: 1,
    ne_name: "",
    ne_ip: "",
    serial_no: "",
    activity_planned: ["I&C"],
    billing_type: "I&C",
    dependency: "N/A",
    visit_type: "I&C",
    engineer_id: engineers[0]?.id || "",
    direct_customer: "",
    end_customer: "",
    customer_spoc: "",
    activity_rate: 3000,
    remarks: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/tasks", form);
      toast.success("Task created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create");
    } finally { setBusy(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#13151A]">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#FF5722]">New Assignment</div>
            <h2 className="text-xl font-semibold">Create Task</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Task Type">
            <select className="input" value={form.task_type} onChange={(e) => set("task_type", e.target.value)} data-testid="tf-task-type">
              {masters?.task_types.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Date">
            <input type="date" className="input" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="tf-date" />
          </Field>
          <Field label="Project">
            <select className="input" value={form.project_name} onChange={(e) => set("project_name", e.target.value)} data-testid="tf-project">
              {projects.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Job ID"><input className="input" value={form.job_id} onChange={(e) => set("job_id", e.target.value)} data-testid="tf-job-id" /></Field>
          <Field label="Circle">
            <select className="input" value={form.circle} onChange={(e) => set("circle", e.target.value)} data-testid="tf-circle">
              {masters?.circles.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="State">
            <select className="input" value={form.state} onChange={(e) => set("state", e.target.value)}>
              {masters?.states.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="City"><input className="input" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label="Site Name"><input className="input" value={form.site_name} onChange={(e) => set("site_name", e.target.value)} data-testid="tf-site-name" /></Field>
          <Field label="Site ID"><input className="input" value={form.site_id} onChange={(e) => set("site_id", e.target.value)} /></Field>
          <Field label="EQPT Brand"><input className="input" value={form.eqpt_brand} onChange={(e) => set("eqpt_brand", e.target.value)} /></Field>
          <Field label="EQPT Type"><input className="input" value={form.eqpt_type} onChange={(e) => set("eqpt_type", e.target.value)} /></Field>
          <Field label="NE Name"><input className="input" value={form.ne_name} onChange={(e) => set("ne_name", e.target.value)} /></Field>
          <Field label="NE IP"><input className="input mono" value={form.ne_ip} onChange={(e) => set("ne_ip", e.target.value)} /></Field>
          <Field label="Serial No"><input className="input mono" value={form.serial_no} onChange={(e) => set("serial_no", e.target.value)} /></Field>
          <Field label="Activity Planned (select multiple)">
            <div className="flex flex-wrap gap-2 border border-white/10 bg-[#0B0C10] p-2 rounded-sm">
              {(masters?.activity_types || masters?.visit_types || []).map((v) => {
                const checked = (form.activity_planned || []).includes(v);
                return (
                  <button
                    type="button"
                    key={v}
                    onClick={() => set("activity_planned", checked ? form.activity_planned.filter((x) => x !== v) : [...(form.activity_planned || []), v])}
                    data-testid={`tf-activity-${v}`}
                    className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border rounded-sm transition-colors ${checked ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-slate-400 hover:border-white/30"}`}
                  >
                    {v}
                  </button>
                );
              })}
              {(masters?.activity_types || []).length === 0 && (
                <span className="text-xs text-slate-500 px-2 py-1">No activity types. Admin → Activity Types → add.</span>
              )}
            </div>
          </Field>
          <Field label="Billing Type">
            <select className="input" value={form.billing_type} onChange={(e) => set("billing_type", e.target.value)} data-testid="tf-billing-type">
              {(masters?.billing_types || []).map((b) => <option key={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Assign Engineer">
            <select className="input" value={form.engineer_id} onChange={(e) => set("engineer_id", e.target.value)} data-testid="tf-engineer">
              <option value="">— Unassigned —</option>
              {engineers.map((e) => <option key={e.id} value={e.id}>{e.name} {e.emp_id ? `(${e.emp_id})` : ""}</option>)}
            </select>
          </Field>
          <Field label="Direct Customer"><input className="input" value={form.direct_customer} onChange={(e) => set("direct_customer", e.target.value)} /></Field>
          <Field label="End Customer"><input className="input" value={form.end_customer} onChange={(e) => set("end_customer", e.target.value)} /></Field>
          <Field label="Customer SPOC"><input className="input" value={form.customer_spoc} onChange={(e) => set("customer_spoc", e.target.value)} /></Field>
          <Field label="Activity Rate (₹)"><input type="number" className="input mono" value={form.activity_rate} onChange={(e) => set("activity_rate", parseFloat(e.target.value) || 0)} /></Field>
          <div className="md:col-span-2">
            <Field label="Remarks"><textarea rows={2} className="input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#13151A]">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm hover:border-white/30">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="tf-submit"
            className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2 disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Task
          </button>
        </div>
      </div>
      <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }
        .input:focus { border-color: #FF5722; outline: none; }`}</style>
    </div>
  );
}
